import { forwardRef, useMemo, useState } from 'react';

import { RiErrorWarningLine } from 'react-icons/ri';

import {
  Box,
  NumberInput as MantineNumberInput,
  NumberInputProps as MantineNumberInputProps
} from '@mantine/core';

import { InputLoader } from '../../feedback';
import { useMergeClassNames } from '../../utils';

import { NumberInputOverrideClassNames } from './NumberInput.styles';

const formatCurrencyLabel = (
  value: number | string | undefined,
  hideDecimals = false
): string | null => {
  if (value === undefined || value === null || value === '') return null;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue) || numValue === 0) return null;

  const absValue = Math.abs(numValue);

  const formatValue = (dividedValue: number) => {
    if (hideDecimals) return dividedValue.toFixed(0);
    // Check if the number has decimals
    const hasDecimals = dividedValue % 1 !== 0;
    return hasDecimals ? dividedValue.toFixed(2) : dividedValue.toFixed(0);
  };

  if (absValue >= 10000000) {
    // Crores (1 crore = 10,000,000)
    const crores = numValue / 10000000;
    const formatted = formatValue(crores);
    return `₹${formatted} ${parseFloat(formatted) === 1 ? 'CRORE' : 'CRORES'}`;
  } else if (absValue >= 100000) {
    // Lakhs (1 lakh = 100,000)
    const lakhs = numValue / 100000;
    const formatted = formatValue(lakhs);
    return `₹${formatted} ${parseFloat(formatted) === 1 ? 'LAKH' : 'LAKHS'}`;
  } else if (absValue >= 1000) {
    // Thousands
    const thousands = numValue / 1000;
    const formatted = formatValue(thousands);
    return `₹${formatted} THOUSAND`;
  }

  return null;
};

interface NumberInputProps extends MantineNumberInputProps {
  loading?: boolean;
  /**
   * ideally not required, added for falcon specific use-cases
   * useCurrencyFormatter is same as thousandSeparator = , and thousandsGroupStyle = 'lakh'
   * setting thousandSeparator or thousandsGroupStyle has no effect when useCurrencyFormatter is set
   */
  useCurrencyFormatter?: boolean;
  shouldShowErrorIcon?: boolean;
  /**
   * Show the value in Indian currency format as end adornment (e.g., "₹10 LAKHS", "₹25 THOUSAND")
   */
  showCurrencyEndAdornment?: boolean;
  /**
   * Hide decimal values in currency end adornment (e.g., "₹10 LAKHS" instead of "₹10.50 LAKHS")
   */
  hideDecimalsInCurrencyLabel?: boolean;
}

/**
 * @param fixedDecimalScale: decimal precision
 * @param hideControls: toggle up/down arrows for step
 * @param step: units to increase or decrease by arrows
 * @param max: maximum number allowed
 * @param min: minimum number allowed
 * @param loading: toggle loading state
 * @param disabled: toggle disabled states
 * @param useCurrencyFormatter: value in input shown with indian currency format,if set takes precedence over thousandsGroupStyle,thousandSeparator
 * @param shouldShowErrorIcon : always show error icon if there is an error
 * @param showCurrencyEndAdornment: show the value in Indian currency format as end adornment (e.g., "₹10 LAKHS", "₹25 THOUSAND")
 * @param hideDecimalsInCurrencyLabel: hide decimal values in currency end adornment (e.g., "₹10 LAKHS" instead of "₹10.50 LAKHS")
 */
const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      size = 'md',
      leftSection,
      rightSection,
      loading,
      error,
      hideControls = true,
      useCurrencyFormatter,
      thousandSeparator,
      thousandsGroupStyle,
      onBlur,
      onFocus,
      onChange,
      value,
      shouldShowErrorIcon = true,
      showCurrencyEndAdornment = false,
      hideDecimalsInCurrencyLabel = false,
      allowNegative = false,
      classNames,
      ...rest
    },
    ref
  ) => {
    const [focussed, setFocussed] = useState<boolean>(false);

    const currencyLabel = useMemo(() => {
      if (!showCurrencyEndAdornment) return null;
      return formatCurrencyLabel(value, hideDecimalsInCurrencyLabel);
    }, [showCurrencyEndAdornment, value, hideDecimalsInCurrencyLabel]);

    const classes = useMemo(() => {
      return NumberInputOverrideClassNames({
        size,
        focussed,
        error,
        rightSection: !!(
          loading ||
          error ||
          currencyLabel ||
          rightSection ||
          shouldShowErrorIcon
        )
      });
    }, [size, focussed, error, loading, currencyLabel, rightSection]);

    const rightSectionFinal = loading ? (
      <InputLoader size={size} />
    ) : error ? (
      shouldShowErrorIcon ? (
        <RiErrorWarningLine />
      ) : (
        rightSection
      )
    ) : currencyLabel ? (
      <Box component="span" className={classes.currencyLabel}>
        {currencyLabel}
      </Box>
    ) : rightSection ? (
      rightSection
    ) : undefined;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    const numValue = useMemo(() => {
      if (value === undefined || value === null || value === '') return 0;
      return typeof value === 'string' ? parseFloat(value) : value;
    }, [value]);

    const sectionClassName = useMemo(() => {
      if (!currencyLabel || error) return undefined;
      const absValue = Math.abs(numValue);
      return {
        section:
          absValue < 100000
            ? classes.sectionWithCurrencySmall
            : classes.sectionWithCurrency
      };
    }, [currencyLabel, numValue, error]);

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, sectionClassName, classNames]
    });

    return (
      <MantineNumberInput
        {...rest}
        ref={ref}
        value={value || value === 0 ? value : ''}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        size={size}
        leftSection={leftSection}
        rightSection={rightSectionFinal}
        error={error}
        hideControls={hideControls}
        decimalSeparator="."
        classNames={mergedClassNames}
        thousandSeparator={useCurrencyFormatter ? ',' : thousandSeparator}
        thousandsGroupStyle={useCurrencyFormatter ? 'lakh' : thousandsGroupStyle}
        allowNegative={allowNegative}
      />
    );
  }
);

export { NumberInput };
export type { NumberInputProps };
